import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GridFour, AirplaneTilt, BellRinging, Stack, PushPin,
  FileArrowDown, GearSix, SignOut, Sun, Moon, ChartLineUp,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { useTheme } from "../../lib/theme";

const NAV = [
  { to: "/", label: "Dashboard", icon: GridFour, testid: "nav-dashboard" },
  { to: "/alerts", label: "Alerts", icon: BellRinging, testid: "nav-alerts" },
  { to: "/data-sources", label: "Data Sources", icon: Stack, testid: "nav-data-sources" },
  { to: "/overrides", label: "Manual Overrides", icon: PushPin, testid: "nav-overrides", admin: true },
  { to: "/reports", label: "Reports", icon: FileArrowDown, testid: "nav-reports" },
  { to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings", admin: true },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex w-64 shrink-0 flex-col"
      style={{
        background: "var(--card-bg)",
        borderRight: "1px solid var(--border-subtle)",
      }}
    >
      {/* Logo */}
      <div className="px-6 pt-6 pb-8">
        <div className="flex items-center gap-3">
          <motion.div
            className="relative w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "var(--saa-gold)" }}
            whileHover={{ rotate: 10 }}
          >
            <AirplaneTilt size={22} weight="duotone" color="#0A1428" className="airplane-anim" />
          </motion.div>
          <div>
            <div className="font-display text-xl font-bold leading-tight tracking-tight">SAA</div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
              Fare Tracker
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1">
        {NAV.filter((n) => !n.admin || isAdmin).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all " +
                (isActive
                  ? "nav-item-active"
                  : "hover:bg-white/5")
              }
              style={({ isActive }) => ({
                color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
              })}
            >
              <Icon size={18} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 space-y-2 border-t" style={{ borderColor: "var(--border-subtle)" }}>
        <button
          data-testid="theme-toggle"
          onClick={toggle}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors"
          style={{ color: "var(--text-secondary)" }}
        >
          {theme === "dark" ? <Sun size={18} weight="duotone" /> : <Moon size={18} weight="duotone" />}
          <span>{theme === "dark" ? "Light theme" : "Dark theme"}</span>
        </button>
        <div className="px-4 py-2 rounded-lg" style={{ background: "var(--card-bg-2)" }}>
          <div className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>
            {user?.email}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] mt-0.5" style={{ color: "var(--saa-gold)" }}>
            {user?.role}
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors"
          style={{ color: "var(--text-secondary)" }}
        >
          <SignOut size={18} weight="duotone" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
